import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import { insightsQueryProcessingStatuses } from "shared/constants";
import { ApplicationRecord } from "shared/services/spraypaint";

import type Article from "./Article";
import type InsightsKeySpokesperson from "./InsightsKeySpokesperson";
import type InsightsKeyTopic from "./InsightsKeyTopic";
import type InsightsQueryFilter from "./InsightsQueryFilter";
import type InsightsReport from "./InsightsReport";
import type Organisation from "./Organisation";
import type RadioClip from "./RadioClip";
import type ReportFile from "./ReportFile";

type InsightQueryStatus =
  | "export_pending"
  | "export_in_progress"
  | "export_completed"
  | "sentiment_rating_pending"
  | "sentiment_rating_in_progress"
  | "sentiment_rating_completed"
  | "error";

type InsightQueryType = "quantitative" | "qualitative" | "insights_plus";

@Model()
class InsightsQuery extends ApplicationRecord {
  static jsonapiType = "insights_queries";

  @Attr() archivedAt: string;

  @Attr() booleanQuery: string;

  @Attr() caseSensitive: boolean;

  @Attr() category: string;

  @Attr() color: string;

  @Attr({ persist: false }) enabledMedia: string[];

  @Attr() excludedCategoryKeys: string[];

  @Attr() excludedLocationKeys: string[];

  @Attr() filterSetIds: number[];

  @Attr() includeOnlineCaldSources: boolean;

  @Attr() includePrintCaldSources: boolean;

  @Attr({ persist: false }) indexing: boolean;

  @HasMany() insightsQueryFilters: InsightsQueryFilter[];

  @Attr() internationalPlus: boolean;

  @HasMany() keySpokespeople: InsightsKeySpokesperson[];

  @HasMany() keyTopics: InsightsKeyTopic[];

  @Attr() label: string;

  @Attr() magazineContent: boolean;

  @Attr() magazineTier: boolean;

  @Attr() mainQuery: boolean;

  @Attr() notes: string;

  @Attr() onlineContent: boolean;

  @Attr() onlineTier: boolean;

  @Attr() organisationName: string;

  @BelongsTo() original: InsightsQuery;

  @Attr() originalId: number;

  @BelongsTo() parent: InsightsReport | Organisation;

  @Attr() parentId: number;

  @Attr() parentName: string;

  @Attr() parentType: string;

  @Attr() podcastContent: boolean;

  @Attr() podcastTier: boolean;

  @Attr() printContent: boolean;

  @Attr() printTier: boolean;

  @Attr() queryType: InsightQueryType;

  @Attr() radioContent: boolean;

  @Attr() radioTier: boolean;

  @HasOne() reportFile: ReportFile;

  @HasMany() reportFiles: ReportFile[];

  @Attr() selectedMagazineContent: any[];

  @Attr() selectedOnlineContent: Article[];

  @Attr() selectedPodcastContent: any[];

  @Attr() selectedPrintContent: string[];

  @Attr() selectedRadioContent: RadioClip[];

  @Attr() selectedSocialContent: any[];

  @Attr() selectedTvContent: string[];

  @Attr() socialContent: boolean;

  @Attr() socialImpactThreshold: number;

  @Attr() socialOnlyVerified: boolean;

  @Attr() socialTier: boolean;

  @Attr() status: InsightQueryStatus;

  @Attr() subTopics: string[];

  @Attr() topicQuery: boolean;

  @Attr() totalMentionsCount: number;

  @Attr() totalMentionsInReview: number;

  @Attr() totalRatedMentions: number;

  @Attr() tvContent: boolean;

  @Attr() tvTier: boolean;

  @Attr() uniqueMentionsCount: number;

  @Attr({ persist: false }) updatedAt: string;

  isCompleted() {
    return ["export_completed", "sentiment_rating_completed"].includes(
      this.status
    );
  }

  isProcessing() {
    return insightsQueryProcessingStatuses.includes(this.status);
  }
}

export default InsightsQuery;
